<template>
  <figure
    v-if="circles.length > 1"
    ref="elRef"
    class="FinancialCircleChart"
  >
    <svg
      class="FinancialCircleChart__svg"
      :height="visualHeight"
    >
      <g
        ref="groupRef"
        :transform="groupTransform"
      >
        <g
          v-for="(circle, index) in circles"
          :key="circle.x + circle.y + circle.r"
          :class="{
            'FinancialCircleChart__circleGroup': true,
            'FinancialCircleChart__circleGroup--isHighlighted': index === highlightedIndex,
          }"
        >
          <circle
            class="FinancialCircleChart__circle"
            :cx="circle.x"
            :cy="circle.y"
            :r="circle.r"
          />

          <text
            text-anchor="middle"
            dominant-baseline="middle"
            class="FinancialCircleChart__circleLabel"
            :x="circle.x"
            :y="circle.y - textSpacing"
            :transform="textTransform"
            :style="{ transformOrigin: `${circle.x}px ${circle.y - textSpacing}px` }"
          >
            {{ getTruncatedLabel(circle.label, circle.r) }}
          </text>

          <text
            text-anchor="middle"
            dominant-baseline="middle"
            class="FinancialCircleChart__circleAmount"
            :x="circle.x"
            :y="circle.y + textSpacing"
            :transform="textTransform"
            :style="{ transformOrigin: `${circle.x}px ${circle.y + textSpacing}px` }"
          >
            {{ circle.formattedAmount }}
          </text>
        </g>
      </g>
    </svg>
  </figure>
</template>

<script lang="ts" setup>
import { Currency, IFinancialInsightsSnapshot, Money } from "@cosine/lib/api-models";
import isTruthy from "@cosine/lib/utils/boolean/isTruthy";
import formatNumberToCompact from "@cosine/lib/utils/number/formatNumberToCompact";
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

const modelValue = defineModel<IFinancialInsightsSnapshot>();

const elRef = useTemplateRef<HTMLDivElement>("elRef");
const groupRef = useTemplateRef<SVGGElement>("groupRef");
const elWidth = ref(0);
const elHeight = ref(0);
const resizeObserver = ref<ResizeObserver | null>(null);

const groupTransform = ref("");
const textTransform = ref("");
const textSpacing = ref(8);
const visualHeight = ref(380);

onMounted(() => {
  if (!elRef.value) return;

  elWidth.value = elRef.value.clientWidth;
  elHeight.value = elRef.value.clientHeight;

  resizeObserver.value = new ResizeObserver(handleResize);
  resizeObserver.value.observe(elRef.value);
});

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect();
});

const chartData = computed(() => {
  if (!modelValue.value?.Metrics) return null;

  const netWorth = modelValue.value.Metrics.find((m) => m.Id === "kpi_net_worth");
  const investmentRateMetric = modelValue.value.Metrics.find((m) => m.Id === "kpi_investment_rate");
  const emergencyFundMetric = modelValue.value.Metrics.find((m) => m.Id === "kpi_emergency_fund");

  const defaultMoney: Money = {
    Amount: 0,
    Currency: Currency.USD,
  };

  const cash = emergencyFundMetric?.SourceValues["cash"] ?? {
    ...defaultMoney,
  };
  const debt = netWorth?.SourceValues["liabilities"] ?? {
    ...defaultMoney,
  };

  let otherAssets: Money;

  if (netWorth?.SourceValues["assets"]) {
    // Only subtract cash if using netWorth.assets
    const assets = netWorth.SourceValues["assets"];
    otherAssets = {
      ...assets,
      Amount: assets.Amount > cash.Amount ? assets.Amount - cash.Amount : assets.Amount,
    };
  } else if (investmentRateMetric?.SourceValues["investments"]) {
    // If falling back to investments, do not subtract cash
    otherAssets = investmentRateMetric.SourceValues["investments"];
  } else {
    otherAssets = {
      ...defaultMoney,
    };
  }

  return {
    cash,
    debt,
    otherAssets,
  };
});

const circles = computed(() => {
  if (!chartData.value) return [];

  const data = ([
    ["Cash", chartData.value.cash],
    ["Debt", chartData.value.debt],
    ["Other Assets", chartData.value.otherAssets],
  ] as Array<[string, Money]>).map(([label, balance]) => {
    if (!balance?.Amount) return null;

    return {
      label,
      amount: balance.Amount,
      formattedAmount: "$" + formatNumberToCompact(balance.Amount / 100),
    };
  }).filter(isTruthy);

  const radii = calculateCircleRadii(data.map((c) => c.amount));

  return packCircles(radii, 400).map((position, index) => ({
    ...position,
    label: data[index].label,
    amount: data[index].amount,
    formattedAmount: data[index].formattedAmount,
  }));
});

const highlightedIndex = computed(() => {
  if (!circles.value.length) return -1;
  const maxAmount = Math.max(...circles.value.map((c) => c.amount));
  return circles.value.findIndex((c) => c.amount === maxAmount);
});

watch(circles, async () => {
  calculateGroupTransform();
}, {
  flush: "post",
});

watch([elWidth, elHeight], async () => {
  calculateGroupTransform();
});

async function handleResize (entries: Array<ResizeObserverEntry>) {
  for (const entry of entries) {
    if (entry.target === elRef.value) {
      elWidth.value = entry.contentRect.width;
      elHeight.value = entry.contentRect.height;
    }
  }
}

function calculateGroupTransform () {
  if (!groupRef.value || !elWidth.value || !elHeight.value) return;

  const bbox = groupRef.value.getBBox();

  if (bbox.width === 0 || bbox.height === 0) return;

  const availableWidth = elWidth.value;
  const availableHeight = elHeight.value;

  const scaleX = availableWidth / bbox.width;
  const scaleY = availableHeight / bbox.height;
  const scale = Math.min(scaleX, scaleY);

  const scaledWidth = bbox.width * scale;
  const scaledHeight = bbox.height * scale;

  const translateX = (elWidth.value - scaledWidth) / 2 - (bbox.x * scale);
  const translateY = (elHeight.value - scaledHeight) / 2 - (bbox.y * scale);

  groupTransform.value = `translate(${translateX}, ${translateY}) scale(${scale})`;

  const inverseScale = 1 / scale;
  textTransform.value = `scale(${inverseScale})`;
  textSpacing.value = 8 * inverseScale;

  visualHeight.value = scaledHeight;
}

function calculateCircleRadii (values: Array<number>) {
  const nonZeroValues = values.filter((v) => v > 0);
  if (nonZeroValues.length === 0) return [];

  const total = nonZeroValues.reduce((sum, val) => sum + val, 0);
  return nonZeroValues.map((val) => Math.sqrt(val / total));
}

function packCircles (radii: Array<number>, containerSize = 400) {
  if (radii.length === 0) return [];
  if (containerSize === 0) return [];

  const minRadius = 32;
  const maxRadius = 120;

  if (radii.length === 1) {
    return [
      {
        x: containerSize / 2,
        y: containerSize / 2,
        r: Math.min(maxRadius, Math.max(minRadius, containerSize / 4)),
      },
    ];
  }

  const center = containerSize / 2;

  const maxRadiusRatio = Math.max(...radii);
  const scaledRadii = radii.map((r) => {
    const normalizedRadius = (r / maxRadiusRatio) * maxRadius;
    return Math.max(minRadius, normalizedRadius);
  });

  let positions: Array<{
    x: number,
    y: number,
    r: number,
  }> = [];

  if (radii.length === 2) {
    const r1 = scaledRadii[0];
    const r2 = scaledRadii[1];
    const distance = r1 + r2;

    positions = [
      {
        x: center - distance / 2,
        y: center,
        r: r1,
      },
      {
        x: center + distance / 2,
        y: center,
        r: r2,
      },
    ];
  } else if (radii.length === 3) {
    const [
      r1,
      r2,
      r3,
    ] = scaledRadii;

    const d12 = r1 + r2;
    const d13 = r1 + r3;
    const d23 = r2 + r3;

    const angleA = Math.acos((d12 * d12 + d13 * d13 - d23 * d23) / (2 * d12 * d13));

    const x1 = 0;
    const y1 = 0;
    const x2 = d12;
    const y2 = 0;
    const x3 = d13 * Math.cos(angleA);
    const y3 = d13 * Math.sin(angleA);

    const centroidX = (x1 + x2 + x3) / 3;
    const centroidY = (y1 + y2 + y3) / 3;

    positions = [
      {
        x: center + x1 - centroidX,
        y: center + y1 - centroidY,
        r: r1,
      },
      {
        x: center + x2 - centroidX,
        y: center + y2 - centroidY,
        r: r2,
      },
      {
        x: center + x3 - centroidX,
        y: center + y3 - centroidY,
        r: r3,
      },
    ];
  } else {
    const angleStep = (2 * Math.PI) / radii.length;
    const baseRadius = Math.max(...scaledRadii);

    positions = scaledRadii.map((r, index) => {
      const angle = index * angleStep;
      return {
        x: center + Math.cos(angle) * baseRadius,
        y: center + Math.sin(angle) * baseRadius,
        r,
      };
    });
  }

  return positions;
}

function getTruncatedLabel (label: string, radius: number): string {
  const padding = 8;
  const availableWidth = (radius * 2) - (padding * 2);

  const averageCharWidth = 8;
  const maxChars = Math.floor(availableWidth / averageCharWidth);

  if (label.length <= maxChars) return label;

  const ellipsis = "…";
  const truncateLength = Math.max(1, maxChars - ellipsis.length);
  return label.substring(0, truncateLength) + ellipsis;
}
</script>

<style lang="scss" scoped>
.FinancialCircleChart {
  position: relative;
  overflow: hidden;
}

.FinancialCircleChart__svg {
  display: block;
  width: 100%;
}

.FinancialCircleChart__circle {
  fill: var(--themeColorBackgroundOffset);
  transition: fill 200ms ease;
}

.FinancialCircleChart__circleGroup--isHighlighted .FinancialCircleChart__circle {
  fill: var(--colorSwissPurple300);
}

.FinancialCircleChart__circleLabel {
  font-family: var(--fontFamilySaans);
  font-size: 14px;
}

.FinancialCircleChart__circleAmount {
  font-family: var(--fontFamilySaans);
  font-size: 14px;
  font-weight: var(--fontWeightSaansMedium);
}
</style>
